import { Box, Chip, Typography } from "@mui/material";
import type { FamilyMemberDto } from "@/types/its-one-view";

const MEMBERS_MAX_HEIGHT = 132;

type FamilyMembersStripProps = {
  members: FamilyMemberDto[];
  hofIts: string;
  hofName: string | null;
  lookupIts: string;
};

function roleShort(hofFmType: string | null, isHof: boolean): string {
  if (isHof) return "HOF";
  if (hofFmType === "FM") return "FM";
  return hofFmType ?? "—";
}

export default function FamilyMembersStrip({
  members,
  hofIts,
  hofName,
  lookupIts,
}: FamilyMembersStripProps) {
  return (
    <Box
      sx={{
        flex: 1,
        minWidth: 0,
        maxWidth: { xs: "100%", md: 560 },
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box sx={{ mb: 1, flexShrink: 0 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 600 }} noWrap>
          {hofName ?? "Household"} · HOF {hofIts}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {members.length} member{members.length === 1 ? "" : "s"}
          {lookupIts !== hofIts ? ` · searched ${lookupIts}` : ""}
        </Typography>
      </Box>

      <Box
        component="ul"
        sx={{
          m: 0,
          p: 0,
          listStyle: "none",
          maxHeight: MEMBERS_MAX_HEIGHT,
          overflowY: "auto",
          overflowX: "hidden",
          border: 1,
          borderColor: "divider",
          borderRadius: 1,
          bgcolor: "background.default",
          "&::-webkit-scrollbar": { width: 8 },
          "&::-webkit-scrollbar-thumb": {
            borderRadius: 4,
            bgcolor: "action.disabled",
          },
        }}
      >
        {members.map((m) => (
          <Box
            component="li"
            key={m.itsId}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              px: 1.25,
              py: 0.75,
              borderBottom: 1,
              borderColor: "divider",
              bgcolor: m.isHof ? "action.hover" : "transparent",
              outline: m.isLookup ? "1px solid" : "none",
              outlineColor: m.isLookup ? "primary.main" : "transparent",
              outlineOffset: -1,
              "&:last-child": { borderBottom: 0 },
            }}
          >
            <Chip
              label={roleShort(m.hofFmType, m.isHof)}
              size="small"
              color={m.isHof ? "primary" : "default"}
              variant={m.isHof ? "filled" : "outlined"}
              sx={{ height: 20, fontSize: "0.65rem", flexShrink: 0 }}
            />
            <Typography
              variant="body2"
              fontFamily="monospace"
              fontWeight={600}
              sx={{ flexShrink: 0, width: 72 }}
            >
              {m.itsId}
            </Typography>
            <Typography
              variant="body2"
              sx={{ flex: 1, minWidth: 0 }}
              noWrap
              title={m.name ?? undefined}
            >
              {m.name ?? "—"}
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ flexShrink: 0, maxWidth: 140 }}
              noWrap
              title={[m.age && `Age ${m.age}`, m.gender, m.mobile].filter(Boolean).join(" · ")}
            >
              {[m.age, m.gender].filter(Boolean).join(" · ") || "—"}
            </Typography>
            {m.isLookup ? (
              <Chip
                label="Searched"
                size="small"
                color="primary"
                variant="outlined"
                sx={{ height: 20, flexShrink: 0 }}
              />
            ) : null}
          </Box>
        ))}
      </Box>
    </Box>
  );
}
